import type {TFunction} from "i18next";

import type {RevaLanguage} from "@reva/shared/i18n";
import {formatIndex, formatValue} from "@reva/shared/number-format";
import type {
    AggregatedTimelineData,
    AggregatedTimelineValue,
    FormattedTimelineRow,
    TimelineObjectType,
    TimelineSegment,
} from "@reva/shared/timelines";
import {formatTimeline, type TimelineRegionalLevel, TimelineSegmentType} from "@reva/shared/timelines";

import {REGIONAL_LEVEL_DATA} from "./timelineAggregation";
import {
    getSupportedRegionalLevels,
    getTimelineDescending,
    getTimelineSegments,
    hasRegionalLevel,
    hasSegment,
} from "./timelineUtil";

export interface TimelineTableData {
    headers: TimelineTableHeaderData[];
    columns: string[];
    rows: object[];
}

export interface TimelineTableHeaderData {
    categoryHeader: string;
    columnHeaders: string[];
}

interface TimelineTableRowBuilder {
    timeline: AggregatedTimelineFormatted;
    timelineColumns: string[];
    mappedColumns: Record<string, string>;
}

export type AggregatedTimelineFormatted = FormattedTimelineRow[];

/**
 * Format timeline.
 * @param timeline
 * @param language language to display
 * @param noFraction render no Fraction digits (e.g. for purchase prices)
 * @return formatted timeline data
 */
const fmtTimeline = (
    timeline: AggregatedTimelineValue[],
    language: RevaLanguage,
    noFraction: boolean,
): AggregatedTimelineFormatted =>
    formatTimeline(
        timeline,
        (value, noFrac) => formatValue(value, language, noFrac),
        (value) => formatIndex(value, language),
        noFraction,
    );

/**
 * Get data for timeline table.
 */
export function getTimelineTableData(
    aggregatedTimelineData: AggregatedTimelineData | null,
    objectType: TimelineObjectType,
    indexed: boolean,
    regionalLevel: TimelineRegionalLevel | null,
    t: TFunction,
    language: RevaLanguage,
) {
    if (!aggregatedTimelineData) return null;
    const {sale: segmentSale, rent: segmentRent} = getTimelineSegments(objectType);
    if (!hasSegment(aggregatedTimelineData, segmentSale) && !hasSegment(aggregatedTimelineData, segmentRent))
        return null;
    if (
        regionalLevel &&
        !getSupportedRegionalLevels(aggregatedTimelineData, segmentSale, segmentRent).includes(regionalLevel)
    ) {
        return null;
    }

    const headers: TimelineTableHeaderData[] = [];
    const columns: string[] = ["year"];
    const rowBuilders: TimelineTableRowBuilder[] = [];

    const unit = indexed ? ` (${t("market.timelines.indexed")})` : " in €/m²";
    const valuePrefix = indexed ? "index" : "value";

    /** Process a possible segment.*/
    const processSegment = (segment: TimelineSegment | null, segmentType: TimelineSegmentType): void => {
        if (hasSegment(aggregatedTimelineData, segment)) {
            const noFraction = segmentType === TimelineSegmentType.SALE;
            if (regionalLevel && hasRegionalLevel(aggregatedTimelineData, segment, regionalLevel)) {
                const timelineData = getTimelineDescending(aggregatedTimelineData, segment, regionalLevel);
                if (!timelineData) return;
                const timeline = fmtTimeline(timelineData, language, noFraction);
                const timelineColumns = [`${valuePrefix}Range`, valuePrefix];
                const mappedColumns: Record<string, string> = {};
                for (const col of timelineColumns) {
                    mappedColumns[col] = segment + regionalLevel + col;
                }

                headers.push({
                    categoryHeader:
                        (segmentType === TimelineSegmentType.RENT
                            ? t("market.timelines.rent_prices")
                            : t("market.timelines.sales_prices")) + unit,
                    columnHeaders: [
                        t("market.timelines.span"),
                        indexed ? t("market.timelines.index") : t("market.timelines.price"),
                    ],
                });
                rowBuilders.push({timeline, timelineColumns, mappedColumns});
            } else if (!regionalLevel) {
                const regionalLevels = getSupportedRegionalLevels(aggregatedTimelineData, segment);
                const columnHeaders: string[] = [];
                for (const regionalLevel of regionalLevels) {
                    const timelineData = getTimelineDescending(aggregatedTimelineData, segment, regionalLevel);
                    if (!timelineData) continue;
                    const timeline = fmtTimeline(timelineData, language, noFraction);
                    const timelineColumns = [valuePrefix];
                    const mappedColumns: Record<string, string> = {};
                    mappedColumns[valuePrefix] = segment + regionalLevel + valuePrefix;

                    columnHeaders.push(t(REGIONAL_LEVEL_DATA[regionalLevel].nameI18n));
                    rowBuilders.push({timeline, timelineColumns, mappedColumns});
                }
                headers.push({
                    categoryHeader:
                        (segmentType === TimelineSegmentType.RENT
                            ? t("market.timelines.rent_prices")
                            : t("market.timelines.sales_prices")) + unit,
                    columnHeaders,
                });
            }
        }
    };

    processSegment(segmentSale, TimelineSegmentType.SALE);
    processSegment(segmentRent, TimelineSegmentType.RENT);

    if (!rowBuilders.length) return null;

    for (const rowBuilder of rowBuilders) {
        const {timelineColumns, mappedColumns} = rowBuilder;
        for (const col of timelineColumns) {
            columns.push(mappedColumns[col]);
        }
    }

    const rows: Record<string, any>[] = rowBuilders[0].timeline.map((tl, index) => {
        const row: Record<string, any> = {year: tl.year};
        for (const rowBuilder of rowBuilders) {
            const {timeline, timelineColumns, mappedColumns} = rowBuilder;
            if (index >= timeline.length) {
                console.warn(`Invalid index ${index}: timelines are not all equally long!`);
                continue;
            }
            for (const col of timelineColumns) {
                row[mappedColumns[col]] = (timeline[index] as Record<string, any>)[col];
            }
        }
        return row;
    });

    return {headers, columns, rows};
}
